import videojs, { VideoJsPlayer } from 'video.js';
import { PlayerSources } from '../player.config';
import { Plugins } from '../player.interface';
import { PlayerError } from '../player-errors';
import { getSources } from '../utils/get-sources';
import { setPlayerSrc } from '../utils/set-player-src';
import { useEffect, useState } from 'react';

export function useSetSource(
  player: VideoJsPlayer,
  sources: PlayerSources,
  plugins: Plugins,
  autoplay: boolean,
  preferredQuality?: string
): { initAutoplay: boolean } {
  const [initAutoplay, setInitAutoplay] = useState(() => autoplay);
  const [videoSrc, setVideoSrc] = useState<videojs.Tech.SourceObject[] | null>(null);

  // get sources
  useEffect(() => {
    const abortController = new AbortController();

    getSources(player, sources, plugins, abortController.signal, preferredQuality)
      .then((videoSrc) => {
        if (abortController.signal.aborted) {
          return;
        }
        setVideoSrc(videoSrc);
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (error instanceof PlayerError) {
          player.error(error.message);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [sources, plugins, preferredQuality, autoplay, player]);

  // set video source
  useEffect(() => {
    // empty sources are allowed, to trigger an player error
    if (!videoSrc) {
      return;
    }

    const currentSrc = player.currentSrc();
    const hasVideoSrc = videoSrc.find((src) => src.src === currentSrc);

    // Do not allow setting the same source again to avoid unnecessary reloading
    if (hasVideoSrc) {
      return;
    }

    const abortController = new AbortController();
    setPlayerSrc(player, videoSrc, autoplay)
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (error instanceof PlayerError) {
          player.error(error.message);
        }
      })
      .finally(() => {
        if (abortController.signal.aborted) {
          return;
        }

        autoplay && setInitAutoplay(false);
      });

    return () => {
      abortController.abort();
    };
  }, [videoSrc, autoplay, player]);

  return { initAutoplay };
}
